import React from 'react';
import { useTheme } from '../context/ThemeContext';
import FeatureIcon from './FeatureIcon';
import type { EventRequirement } from '../lib/eventCharacteristics';

interface Props {
  requirements: EventRequirement[];
}

const EXCLUDED_PREFIX = 'Excluded ';

/* Canonical MotorcycleColor paint values (RidePulse color_palettes.json
   standard set) — used only to render a swatch beside the named color. */
const EVENT_BIKE_COLORS: Record<string, string> = {
  Black: '#000000',
  White: '#FFFFFF',
  Red: '#FF0000',
  Blue: '#0066CC',
  Silver: '#C0C0C0',
  Gray: '#808080',
  Green: '#228B22',
  Yellow: '#FFD700',
  Orange: '#FF6600',
  Purple: '#6A0DAD',
  Brown: '#8B4513',
};

const EventRequirements: React.FC<Props> = ({ requirements }) => {
  const { colors, dsColors } = useTheme();
  if (!requirements.length) return null;

  const riders = requirements.filter(
    (r) => r.group === 'Experience' || r.group === 'Who can join'
  );
  const motoPositive = requirements.filter(
    (r) => r.group === 'Motorcycle' && !r.label.startsWith(EXCLUDED_PREFIX)
  );
  const motoExcluded = requirements.filter(
    (r) => r.group === 'Motorcycle' && r.label.startsWith(EXCLUDED_PREFIX)
  );

  const groupHeading = (icon: Parameters<typeof FeatureIcon>[0]['name'], text: string, iconColor: string) => (
    <h3 className="shared-event__req-heading" style={{ color: colors.textSecondary }}>
      <span style={{ color: iconColor }} aria-hidden="true">
        <FeatureIcon name={icon} size={14} />
      </span>
      <span>{text}</span>
    </h3>
  );

  const renderValue = (label: string, value: string) => {
    if (label !== 'Color') return value;
    const names = value.split(' or ');
    return names.map((name, i) => (
      <React.Fragment key={name}>
        {i > 0 && ' or '}
        {Object.prototype.hasOwnProperty.call(EVENT_BIKE_COLORS, name) && (
          <span
            className="shared-event__color-dot"
            style={{ backgroundColor: EVENT_BIKE_COLORS[name], borderColor: colors.border }}
            aria-hidden="true"
          />
        )}
        {name}
      </React.Fragment>
    ));
  };

  const facts = (items: EventRequirement[], stripExcluded = false) => (
    <dl className="shared-event__req-facts">
      {items.map((item) => {
        const label = stripExcluded
          ? item.label.charAt(EXCLUDED_PREFIX.length).toUpperCase() +
            item.label.slice(EXCLUDED_PREFIX.length + 1)
          : item.label;
        return (
          <div key={`${item.label}:${item.value}`} className="shared-event__req-fact">
            <dt className="shared-event__req-label" style={{ color: colors.textTertiary }}>
              {label}
            </dt>
            <dd className="shared-event__req-value" style={{ color: colors.textPrimary }}>
              {renderValue(label, item.value)}
            </dd>
          </div>
        );
      })}
    </dl>
  );

  return (
    <section
      className="shared-event__requirements shared-event__panel"
      aria-labelledby="event-requirements-heading"
    >
      <h2
        id="event-requirements-heading"
        className="shared-event__req-title"
        style={{ color: colors.textTertiary }}
      >
        Event requirements
      </h2>
      {riders.length > 0 && (
        <div className="shared-event__req-block">
          {groupHeading('users', 'Riders', dsColors.signalBlue)}
          {facts(riders)}
        </div>
      )}
      {(motoPositive.length > 0 || motoExcluded.length > 0) && (
        <div className="shared-event__req-block">
          {groupHeading('gauge', 'Motorcycles', dsColors.warningStrong)}
          {motoPositive.length > 0 && facts(motoPositive)}
          {motoExcluded.length > 0 && (
            <div className="shared-event__req-exclusions">
              {groupHeading('mute', 'Exclusions', dsColors.warningStrong)}
              {facts(motoExcluded, true)}
            </div>
          )}
        </div>
      )}
    </section>
  );
};

export default EventRequirements;
